<template>
  <div class="flex w-full grow flex-row md:h-full">
    <div
      class="h-full basis-1/4 overflow-y-auto overflow-x-hidden border-r border-light-4 dark:border-dark-2"
      data-testId="radiatorTabs"
    >
      <template v-if="hasGroups">
        <template v-for="group in groupedTabs" :key="group.name">
          <div
            class="cursor-pointer px-2 py-4 font-bold"
            :class="{
              'text-primary-2 dark:text-primary-2': openGroup === group.name,
            }"
            @click="
              openGroup = group.name;
              selectedTitle = group.tabs[0]?.title;
            "
          >
            {{ group.name }}
          </div>
          <template v-if="openGroup === group.name">
            <div
              v-for="tab in group.tabs"
              :key="tab.title"
              :data-testId="`${tab.title}_tab`"
              :class="[
                'cursor-pointer px-4 py-2 text-sm',
                {
                  'bg-light-3 dark:bg-dark-3 text-primary-2 dark:text-primary-2':
                    tab.title === selectedTitle,
                },
              ]"
              @click="selectedTitle = tab.title"
            >
              {{ tab.title }}
            </div>
          </template>
        </template>

        <div
          v-for="tab in ungroupedTabs"
          :key="tab.title"
          :data-testId="`${tab.title}_tab`"
          :class="[
            'flex cursor-pointer px-2 py-4 text-base',
            {
              'bg-light-3 text-primary-2 dark:bg-dark-3 dark:text-primary-2':
                tab.title === selectedTitle,
            },
          ]"
          @click="selectedTitle = tab.title"
        >
          <span
            :class="[{ 'mdi mdi-check pr-2': !tab.validation?.$invalid }]"
          />
          {{ tab.title }}
        </div>
      </template>

      <template v-else>
        <div
          v-for="tab in tabs"
          :key="tab.title"
          :data-testId="`${tab.title}_tab`"
          :class="[
            'flex cursor-pointer px-2 py-4 text-base',
            {
              'bg-light-3 text-primary-2 dark:bg-dark-3 dark:text-primary-2':
                tab.title === selectedTitle,
            },
          ]"
          @click="selectedTitle = tab.title"
        >
          <span
            :class="[{ 'mdi mdi-check pr-2': !tab.validation?.$invalid }]"
          />
          {{ tab.title }}
        </div>
      </template>
    </div>
    <div class="h-full basis-3/4 overflow-y-auto px-5 py-3">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, provide, Ref, onMounted, computed } from 'vue';
import { useSlots } from 'vue';
import { TabProps } from '@/components/UI/Tabs/Tab.vue';

const slots = useSlots();
const tabs: Ref<Array<TabProps>> = ref([]);
const tabTitles: Ref<Array<string>> = ref([]);
const selectedTitle: Ref<string | undefined> = ref(undefined);
const openGroup: Ref<string | undefined> = ref(undefined);

const groupedTabs = computed(() => {
  const groups: Array<{ name: string; tabs: TabProps[] }> = [];
  const seen = new Set<string>();
  for (const tab of tabs.value) {
    if (!tab.group) continue;
    if (!seen.has(tab.group)) {
      seen.add(tab.group);
      groups.push({ name: tab.group, tabs: [] });
    }
    groups.find((g) => g.name === tab.group)!.tabs.push(tab);
  }
  return groups;
});

const ungroupedTabs = computed(() => tabs.value.filter((t) => !t.group));
const hasGroups = computed(() => groupedTabs.value.length > 0);

onMounted(() => {
  if (!slots.default) return;
  tabs.value = slots
    .default()
    .map((node) => node.props)
    .filter((props): props is TabProps => props !== null);
  tabTitles.value = tabs.value.map((tab) => tab.title);

  const firstGrouped = tabs.value.find((t) => t.group);
  if (firstGrouped) {
    openGroup.value = firstGrouped.group;
    selectedTitle.value = firstGrouped.title;
  } else {
    selectedTitle.value = tabTitles.value[0];
  }
});

provide('selectedTitle', selectedTitle);
</script>
